import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConnectedPartners } from 'services/repositories/partners/partners';
import {
  GetInboxStatsResponse,
  useDashboardStats,
  useFailedTransactionsCount,
  useInboxStats,
} from 'services/repositories/stats/stats';
import { useCurrentUser } from 'services/repositories/user/user';
import { cn } from 'support/helpers/cn/cn';
import { DocumentType } from 'support/types';
import { RelationshipsStats, TradePartnerStats, TransactionGraphData } from 'support/types/dtos';
import { Page } from 'components/Page/Page';

import { DateRange, TransactionVolumeStats } from '../types';
import { ConnectionsStatsDisplay } from './ConnectionsStatsDisplay/ConnectionsStatsDisplay';
import { FailedTransactions } from './FailedTransactions/FailedTransactions';
import { InboxOverview } from './Inbox/Inbox';
import { TradePartnerStatsDisplay } from './TradePartnerStatsDisplay/TradePartnerStatsDisplay';
import { TransactionGraph } from './TransactionGraph/TransactionGraph';
import { TransactionVolume } from './TransactionVolume/TransactionVolume';

export function GraphsDashboard() {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<TransactionVolumeStats['type']>();
  const [selectedPartner, setSelectedPartner] = useState<string>();
  const [currentDate] = useState(() => new Date(Date.now()).setUTCHours(0, 0, 0, 0));
  const dateRangeOptions: Array<DateRange> = useMemo(
    () => [
      {
        value: currentDate - 1000 * 60 * 60 * 24 * 7,
        interval: 1000 * 60 * 60 * 24 * 7,
        label: t('dashboard:transactions.lastSevenDays'),
        testId: 'dateRange-week',
      },
      {
        value: currentDate - 1000 * 60 * 60 * 24 * 30,
        interval: 1000 * 60 * 60 * 24 * 30,
        label: t('dashboard:transactions.lastThirtyDays'),
        testId: 'dateRange-month',
      },
      {
        value: currentDate - 1000 * 60 * 60 * 24 * 90,
        interval: 1000 * 60 * 60 * 24 * 90,
        label: t('dashboard:transactions.lastThreeMonths'),
        testId: 'dateRange-3-months',
      },
      {
        value: currentDate - 1000 * 60 * 60 * 24 * 180,
        interval: 1000 * 60 * 60 * 24 * 180,
        label: t('dashboard:transactions.lastSixMonths'),
        testId: 'dateRange-6-months',
      },
      {
        value: currentDate - 1000 * 60 * 60 * 24 * 365,
        interval: 1000 * 60 * 60 * 24 * 365,
        label: t('dashboard:transactions.lastYear'),
        testId: 'dateRange-year',
      },
    ],
    [currentDate, t],
  );
  const [dateRange, setDateRange] = useState<number>(dateRangeOptions[0].value);
  const [previousDateRange, setPreviousDateRange] = useState<number>(dateRangeOptions[0].interval);

  // Derive the dropdown's selected interval in days so the backend can scope
  // its `graphData` CTE to just that range instead of the partner's entire
  // history. Falls back to the first option (7 days) if dateRange somehow
  // doesn't match any option.
  const sinceDays = useMemo(() => {
    const matched = dateRangeOptions.find((option) => option.value === dateRange) ?? dateRangeOptions[0];
    return Math.round(matched.interval / (1000 * 60 * 60 * 24));
  }, [dateRange, dateRangeOptions]);

  const { data: currentUser } = useCurrentUser();
  const { data: connectedTradePartners, isInitialLoading: connectedPartnersLoading } = useConnectedPartners();
  const { data: dashboardOverviewStats } = useDashboardStats({
    variables: {
      relatedPartnerId: undefined,
      sinceDays,
    },
    select: (data) => {
      return {
        tradePartnerStats: data.tradePartnerStats as TradePartnerStats,
        relationshipsStats: data.relationshipsStats as RelationshipsStats,
      };
    },
  });

  const {
    data: dashboardStatsData,
    isInitialLoading: dashboardStatsLoading,
    isFetching: dashboardStatsRefetching,
  } = useDashboardStats({
    variables: {
      relatedPartnerId: selectedPartner,
      sinceDays,
    },
  });

  const { data: failedTransactionCountData, isLoading: areFailedTransactionsLoading } = useFailedTransactionsCount();
  const { data: inboxStatsData, isLoading: loadingInboxStatsData } = useInboxStats({
    staleTime: 0, //make sure to override our default options
    refetchOnMount: true, //make sure to override our default options
  });

  useEffect(() => {
    const index = dateRangeOptions.findIndex((date) => date.value === dateRange);
    const newPreviousDateRange = index > -1 ? dateRangeOptions[index].interval : dateRangeOptions[0].interval;
    setPreviousDateRange(newPreviousDateRange);
  }, [dateRange, dateRangeOptions]);

  const transactionsOnGraph = useMemo(() => {
    return filterTransactionsByType(dashboardStatsData?.transactions, selectedType);
  }, [dashboardStatsData?.transactions, selectedType]);
  const actionRequiredTransactions = failedTransactionCountData?.actionRequiredTransactions;
  const inboxIsActive = inboxStatsData?.active;
  const { relationshipsStats, tradePartnerStats } = dashboardOverviewStats ?? {};

  return (
    <Page isLoading={areFailedTransactionsLoading || loadingInboxStatsData}>
      <Page.Head title={t('dashboard:title', { name: currentUser?.name })} />

      <Page.Section>
        <div className={cn('grid gap-x-12', inboxIsActive ? 'grid-cols-3' : 'grid-cols-2')}>
          <div className="col-span-2 flex flex-col gap-y-6">
            <div className={cn('grid gap-6', getFailedTransactionsGridColumns(actionRequiredTransactions))}>
              {actionRequiredTransactions ? (
                <FailedTransactions failedTransactionsCount={actionRequiredTransactions} />
              ) : null}

              <TradePartnerStatsDisplay
                numberOfReadyPartners={tradePartnerStats?.ready ?? 0}
                numberOfTestingPartners={tradePartnerStats?.inTesting ?? 0}
              />

              <ConnectionsStatsDisplay numberOfReadyConnections={relationshipsStats?.ready ?? 0} />
            </div>

            <div
              className={cn(
                'flex flex-col gap-y-6 transition-opacity duration-150',
                // During a background refetch (e.g. date dropdown change) keep
                // the previous chart visible but subtly dim it so the user
                // knows fresh data is loading. `isInitialLoading` handles the
                // first-load skeleton separately.
                dashboardStatsRefetching && !dashboardStatsLoading ? 'opacity-60' : '',
              )}
              aria-busy={dashboardStatsRefetching && !dashboardStatsLoading}
            >
              <TransactionVolume
                transactions={dashboardStatsData?.transactions}
                dateRangeOptions={dateRangeOptions}
                volumeDateRange={dateRange}
                previousDateRange={previousDateRange}
                onUpdateDateRange={setDateRange}
                selectedTransactionType={selectedType}
                onSelectTransactionType={setSelectedType}
                partnerOptions={connectedTradePartners?.map((partner) => ({
                  label: partner.name,
                  value: partner.id,
                }))}
                isLoadingPartners={connectedPartnersLoading}
                selectedPartner={selectedPartner}
                onSelectPartner={setSelectedPartner}
              />

              <TransactionGraph
                transactions={transactionsOnGraph}
                dateRangeOptions={dateRangeOptions}
                chartDateRange={dateRange}
                loading={dashboardStatsLoading}
              />
            </div>
          </div>

          <InboxOverviewSection inboxStatsData={inboxStatsData} />
        </div>
      </Page.Section>
    </Page>
  );
}

const filterTransactionsByType = (transactions: TransactionGraphData | undefined, type: string | undefined) => {
  if (!transactions) {
    return transactions;
  }
  switch (type) {
    case DocumentType.order:
      return { orders: transactions.orders };
    case DocumentType.shippingNotice:
      return { shippingNotices: transactions.shippingNotices };
    case DocumentType.invoice:
      return { invoices: transactions.invoices };
    case DocumentType.dispatchInstruction:
      return { dispatchInstructions: transactions.dispatchInstructions };
    case DocumentType.dispatchResponse:
      return { dispatchInstructionResponses: transactions.dispatchInstructionResponses };
    default:
      return transactions;
  }
};

const InboxOverviewSection = ({ inboxStatsData }: { inboxStatsData: GetInboxStatsResponse | undefined }) =>
  inboxStatsData?.active ? (
    <div>
      <InboxOverview inboxStatsData={inboxStatsData} />
    </div>
  ) : null;

const getFailedTransactionsGridColumns = (actionRequiredTransactions: number | undefined): string =>
  actionRequiredTransactions ? 'grid-cols-3' : 'grid-cols-2';
